/**
 * @name getCodeLineStyle
 * @description allows for dimming certain lines of code in code blocks to hightlight only what is important.
 * @example
 *  getCodeLineStyle(4, [1,2,3]) -> { opacity: 0.4 }
 *  getCodeLineStyle(1, [1,2,3]) -> undefined
 *  getCodeLineStyle(1, []) -> undefined
 */
export const getCodeLineStyle = (
  lineNumber: number,
  linesToHighlight: number[],
): { opacity: number } | undefined => {
  if (!linesToHighlight.length) return

  const isHighlightedLine = linesToHighlight.includes(lineNumber)

  if (!isHighlightedLine) return { opacity: 0.4 }
}

/**
 * @name createLineNumberArray
 * @description used for translating a string of line numbers into an array of numbers.
 * @example createLineNumberArray("1-3, 5, 10") -> [1, 2, 3, 5, 10]
 */
export const createLineNumberArray = (lineNumbers: string): number[] => {
  let lines: number[] = []

  // Return empty array if empty string is passed
  if (!lineNumbers.length) return []

  lineNumbers.split(',').forEach((value: string) => {
    // If value does not include a "-", that means it should be a standalone number
    if (!value.includes('-')) {
      return lines.push(Number(value))
    }

    const rangeStringArray = value.split('-')
    const min = Number(rangeStringArray[0])
    const max = Number(rangeStringArray[1])

    // Create array of numbers from min to max
    const rangeNumberArray = Array.from(
      { length: max - min + 1 },
      (_, i) => min + i,
    )

    lines = [...lines, ...rangeNumberArray]
  })

  return lines
}

export function getLineNumbers(code: string): number[] {
  if (!code) return [1]
  // Split by \n, count lines, even if last line is empty (trailing \n)
  const lines = code.split('\n')
  return Array.from({ length: lines.length }, (_, i) => i + 1)
}

export function highlightLines(
  code: string,
  highlights: number[],
): { line: number; highlighted: boolean }[] {
  const lineNumbers = getLineNumbers(code)
  return lineNumbers.map((line) => ({
    line,
    highlighted: highlights.includes(line),
  }))
}

export function parseHighlightMeta(meta: string): number[] {
  const match = meta.match(/^\{([\d,\-\s]+)\}$/)
  if (!match) return []

  const result: number[] = []
  const parts = match[1].split(',').map((s) => s.trim())

  for (const part of parts) {
    if (/^\d+$/.test(part)) {
      result.push(Number(part))
    } else if (/^(\d+)-(\d+)$/.test(part)) {
      const [, start, end] = part.match(/^(\d+)-(\d+)$/)!
      for (let i = Number(start); i <= Number(end); i++) {
        result.push(i)
      }
    }
  }

  return result
}
